import React from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { InventoryBriefDTO } from '@recurrency/core-api-schema/dist/inventory/getInventoryBrief';
import { theme } from 'theme';

import { ItemLink, PurchasingVendorLink, SalesCustomerLink } from 'components/Links';
import { ChangeDeltaWithArrow } from 'components/recipes/ChangeDeltaWithArrow';

import { formatIdNameObj, formatNumber, formatUSD } from 'utils/formatting';
import {
  ExportColumn,
  sortableChangeDollarColumn,
  sortableChangeNumberColumn,
  sortableChangePercentColumn,
  sortableDollarColumn,
  sortableIdColumn,
  sortableNumberColumn,
  sortableStringColumn,
} from 'utils/tables';

export interface InventoryBriefPanelRow {
  id: string;
  /** Entity name, wrapped in a link to its details page when the tenant has access */
  name: React.ReactNode;
  change: React.ReactNode;
  changePercent?: number;
}

export interface InventoryBriefCategoryConfig {
  key: keyof InventoryBriefDTO;
  title: string;
  description: string;
  /** Endpoint returning the top 100 records for this category */
  detailSchema: Any;
  rowKey: string | ((record: Any) => string);
  detailColumns: ExportColumn<Any>[];
  getPanelRows: (brief: InventoryBriefDTO, showLinks: boolean) => InventoryBriefPanelRow[];
}

const dollarChange = (value: number) => <ChangeDeltaWithArrow value={value} valueFormatter={formatUSD} />;
const daysChange = (value: number) => (
  <ChangeDeltaWithArrow value={value} valueFormatter={(val) => `${formatNumber(val, 1)} days`} />
);

// the item details page is keyed by item code, so items without a code have no page to link to
const itemNameLink = (item: { itemCode?: string; itemName: string }) =>
  item.itemCode ? <ItemLink itemCode={item.itemCode}>{item.itemName}</ItemLink> : item.itemName;

// locations have no details page, so surface the location as plain text next to the item name
const deadstockPanelName = (item: { itemCode?: string; itemName: string; locationId?: string }, showLinks: boolean) => (
  <>
    {showLinks ? itemNameLink(item) : item.itemName}
    {item.locationId ? (
      <span
        className={css`
          color: ${theme.colors.neutral[400]};
        `}
      >
        {' '}
        · {item.locationId}
      </span>
    ) : null}
  </>
);

const formatLocation = (record: { locationId?: string; locationName?: string }) =>
  record.locationId ? formatIdNameObj({ foreignId: record.locationId, name: record.locationName ?? '' }) : '';

const locationColumn = (): ExportColumn<Any> =>
  sortableStringColumn({
    title: 'Location',
    dataIndex: 'locationId',
    render: (_: string | undefined, record: Any) => formatLocation(record),
    exportValue: (record: Any) => formatLocation(record),
  });

const itemColumns: ExportColumn<Any>[] = [
  sortableIdColumn({
    title: 'Item ID',
    dataIndex: 'itemCode',
    // the item details page is keyed by item code, so items without a code have no page to link to
    render: (itemCode: string | undefined, record: Any) =>
      itemCode ? <ItemLink itemCode={itemCode}>{itemCode}</ItemLink> : record.itemId,
    exportValue: (record: Any) => record.itemCode ?? record.itemId,
  }),
  sortableStringColumn({ title: 'Item Name', dataIndex: 'itemName' }),
];

const changePercentColumn = (): ExportColumn<Any> =>
  sortableChangePercentColumn({
    title: 'Change %',
    dataIndex: 'changePercent',
    exportType: 'number' as const,
    exportValue: (record: Any) => record.changePercent ?? '',
  });

const salesComparisonColumns = (changeDefaultSortOrder: 'ascend' | 'descend'): ExportColumn<Any>[] => [
  sortableDollarColumn({ title: 'Sales (Last 12M)', dataIndex: 'currentSales', exportType: 'number' as const }),
  sortableDollarColumn({ title: 'Sales (Prior 12M)', dataIndex: 'priorSales', exportType: 'number' as const }),
  sortableChangeDollarColumn({
    title: 'Change',
    dataIndex: 'change',
    defaultSortOrder: changeDefaultSortOrder,
    exportType: 'number' as const,
  }),
  changePercentColumn(),
];

export const inventoryBriefCategories: InventoryBriefCategoryConfig[] = [
  {
    key: 'churningCustomers',
    title: 'Churning Customers',
    description: 'Customers with the largest sales declines over the last 12 months compared to the prior 12 months',
    detailSchema: schemas.inventory.getInventoryBriefChurningCustomers,
    rowKey: 'customerId',
    detailColumns: [
      sortableIdColumn({
        title: 'Customer',
        dataIndex: 'customerId',
        render: (customerId: string) => <SalesCustomerLink customerId={customerId}>{customerId}</SalesCustomerLink>,
      }),
      sortableStringColumn({ title: 'Customer Name', dataIndex: 'customerName' }),
      ...salesComparisonColumns('ascend'),
    ],
    getPanelRows: (brief, showLinks) =>
      brief.churningCustomers.items.map((item) => ({
        id: item.customerId,
        name: showLinks ? (
          <SalesCustomerLink customerId={item.customerId}>{item.customerName}</SalesCustomerLink>
        ) : (
          item.customerName
        ),
        change: dollarChange(item.change),
        changePercent: item.changePercent,
      })),
  },
  {
    key: 'decliningItems',
    title: 'Declining Items',
    description: 'Items with the largest sales declines over the last 12 months compared to the prior 12 months',
    detailSchema: schemas.inventory.getInventoryBriefDecliningItems,
    rowKey: 'itemId',
    detailColumns: [...itemColumns, ...salesComparisonColumns('ascend')],
    getPanelRows: (brief, showLinks) =>
      brief.decliningItems.items.map((item) => ({
        id: item.itemId,
        name: showLinks ? itemNameLink(item) : item.itemName,
        change: dollarChange(item.change),
        changePercent: item.changePercent,
      })),
  },
  {
    key: 'increasingItems',
    title: 'Increasing Items',
    description: 'Items with the largest sales increases over the last 12 months compared to the prior 12 months',
    detailSchema: schemas.inventory.getInventoryBriefIncreasingItems,
    rowKey: 'itemId',
    detailColumns: [...itemColumns, ...salesComparisonColumns('descend')],
    getPanelRows: (brief, showLinks) =>
      brief.increasingItems.items.map((item) => ({
        id: item.itemId,
        name: showLinks ? itemNameLink(item) : item.itemName,
        change: dollarChange(item.change),
        changePercent: item.changePercent,
      })),
  },
  {
    key: 'increasingDeadstock',
    title: 'Increasing Deadstock',
    description:
      'Items with the largest increases in average deadstock value over the last 12 months compared to the prior 12 months',
    detailSchema: schemas.inventory.getInventoryBriefIncreasingDeadstock,
    rowKey: (record: Any) => `${record.itemId}-${record.locationId ?? ''}`,
    detailColumns: [
      ...itemColumns,
      locationColumn(),
      sortableDollarColumn({
        title: 'Deadstock Value (Last 12M)',
        dataIndex: 'currentValue',
        exportType: 'number' as const,
      }),
      sortableDollarColumn({
        title: 'Deadstock Value (Prior 12M)',
        dataIndex: 'priorValue',
        exportType: 'number' as const,
      }),
      sortableNumberColumn({
        title: 'Deadstock Qty (Last 12M)',
        dataIndex: 'currentQty',
        exportType: 'number' as const,
      }),
      sortableNumberColumn({
        title: 'Deadstock Qty (Prior 12M)',
        dataIndex: 'priorQty',
        exportType: 'number' as const,
      }),
      sortableChangeDollarColumn({
        title: 'Change',
        dataIndex: 'change',
        defaultSortOrder: 'descend',
        exportType: 'number' as const,
      }),
      changePercentColumn(),
    ],
    getPanelRows: (brief, showLinks) =>
      brief.increasingDeadstock.items.map((item) => ({
        id: `${item.itemId}-${item.locationId ?? ''}`,
        name: deadstockPanelName(item, showLinks),
        change: dollarChange(item.change),
        changePercent: item.changePercent,
      })),
  },
  {
    key: 'suppliersIncreasingLeadTimes',
    title: 'Suppliers Increasing Lead Times',
    description:
      'Suppliers whose average lead times over the last 12 months have grown compared to the prior 12 months',
    detailSchema: schemas.inventory.getInventoryBriefSuppliersLeadTimes,
    rowKey: 'supplierId',
    detailColumns: [
      sortableIdColumn({
        title: 'Supplier',
        dataIndex: 'supplierId',
        render: (supplierId: string) => <PurchasingVendorLink vendorId={supplierId}>{supplierId}</PurchasingVendorLink>,
      }),
      sortableStringColumn({ title: 'Supplier Name', dataIndex: 'supplierName' }),
      sortableNumberColumn({
        title: 'Avg Lead Time (Last 12M)',
        dataIndex: 'currentAvgLeadTimes',
        render: (days: number) => `${formatNumber(days, 1)} days`,
        exportType: 'number' as const,
      }),
      sortableNumberColumn({
        title: 'Avg Lead Time (Prior 12M)',
        dataIndex: 'priorAvgLeadTimes',
        render: (days: number) => `${formatNumber(days, 1)} days`,
        exportType: 'number' as const,
      }),
      sortableChangeNumberColumn({
        title: 'Change',
        dataIndex: 'change',
        defaultSortOrder: 'descend',
        render: (value: number) => daysChange(value),
        exportType: 'number' as const,
      }),
      changePercentColumn(),
    ],
    getPanelRows: (brief, showLinks) =>
      brief.suppliersIncreasingLeadTimes.items.map((item) => ({
        id: item.supplierId,
        name: showLinks ? (
          <PurchasingVendorLink vendorId={item.supplierId}>{item.supplierName}</PurchasingVendorLink>
        ) : (
          item.supplierName
        ),
        change: daysChange(item.change),
        changePercent: item.changePercent,
      })),
  },
];
